/**
 * ItemTable — Premium design with TanStack Virtual.
 *
 * High-density table with virtual scrolling, selectable rows,
 * sortable columns, group headers, and refined visual design.
 */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  MoreHorizontal,
  GitBranch,
  Link2,
  ExternalLink,
  Copy,
  Clock,
  Archive,
  Check,
  Pencil,
  ChevronRight,
  Layers,
} from 'lucide-react';
import { ListTable, type ListTableRanked } from '../../../../components/collection/ListTable';
import type { ItemRow, GroupedRows } from '../hooks/useItemListData';
import type { DensityMode, SortSpec } from '../hooks/useItemListState';
import { ALL_COLUMNS } from '../hooks/useItemListState';
import { COLUMN_CONFIG, type ColConfig } from '../config/itemFields';
import { useAttributesStore } from '../../../../stores/prioritization/attributesStore';
import { AltitudeIndicator } from '../../../../components/ui/AltitudeIndicator';
import { ItemCreationProvenanceBadge } from './ItemCreationProvenanceBadge';
import { authorshipNeedsMark } from '../../../../types/prioritization/creationProvenance';
import { useItemsStore } from '../../../../stores/prioritization/itemsStore';
import { ALTITUDE_LEVELS_ORDERED } from '../../../../types/prioritization/itemLifecycle';
import { useAltitudeText } from '../../../../hooks/prioritization/useAltitudeText';

// ============================================================================
// Visual constants
// ============================================================================


const STATE_LABELS: Record<string, string> = {
  // New exit_status values
  active:   'Active',
  deferred: 'Deferred',
  archived: 'Archived',
  // Legacy aliases (kept so old data rows still display something reasonable)
  RAW: 'Active', TRIAGED: 'Active', DIRECT: 'Active',
  SHAPING: 'Active', SHAPED: 'Active',
  VALUES_PROPOSED: 'Active', RECONCILED: 'Active',
  PRIORITIZED: 'Active', FOCUS: 'Active', LOCKED: 'Active',
  DEFERRED: 'Deferred', ARCHIVED: 'Archived',
};

// Altitude names are now category-specific — use getAltitudeName(level, category)

// ============================================================================
// Column configuration — drives ordered rendering
// ============================================================================
//
// COLUMN_CONFIG and ColConfig are derived from / defined by the itemFields
// registry (config/itemFields.ts) and imported above. `triage_action` is a
// table-only column (not in the picker) and is included there.

// Single-source label lookup — ALL_COLUMNS is the canonical human label for each key.
// COLUMN_CONFIG.label is the fallback for columns that only exist in the table (e.g. triage_action).
const ALL_COLUMNS_LABEL: Record<string, string> = Object.fromEntries(
  ALL_COLUMNS.map((c) => [c.key, c.label])
);

// Color-coded chips — icons/colors from relationshipGuide (canonical registry)
import { getRelationshipGuideEntry, relationshipChipClass } from '../../../../lib/relationshipGuide';
import { ValueMark } from '@/components/ds';
import type { ValueMarkView } from '../../../../lib/valueMark';

const REL_CHIPS: Array<{ field: string; guideId: string }> = [
  { field: 'hasCluster',     guideId: 'cluster' },
  { field: 'hasPackage',     guideId: 'package' },
  { field: 'hasCompeting',   guideId: 'exclusive' },
  // Sequence / composition / elaboration chips use role-aware fields; show when either role is present.
  { field: 'hasPredecessors', guideId: 'sequence' },
  { field: 'hasDependents',   guideId: 'sequence' },
  { field: 'hasCompositionParts', guideId: 'composition' },
  { field: 'isPartOfComposition', guideId: 'composition' },
  { field: 'hasElaborationPossibilities', guideId: 'elaboration' },
  { field: 'isElaborationPossibility', guideId: 'elaboration' },
  { field: 'hasCollection',  guideId: 'collection' },
  { field: 'hasLineage',     guideId: 'lineage' },
  { field: 'hasFrames',      guideId: 'reframe' },
];

// ============================================================================
// Props
// ============================================================================

/** Who decided an item's category and altitude (lib/valueMark). */
export interface ClassificationMarks {
  category: ValueMarkView | null;
  altitude: ValueMarkView | null;
}

interface ItemTableProps {
  rows: ItemRow[];
  grouped: GroupedRows[] | null;
  density: DensityMode;
  selectedRows: Set<string>;
  onToggleRow: (id: string) => void;
  onSelectAll: (ids: string[]) => void;
  /** Select or clear a set of rows (a whole group) without touching the rest. */
  onSelectRows?: (ids: string[], selected: boolean) => void;
  onClearSelection: () => void;
  onRowClick: (item: ItemRow) => void;
  /** The row menu's acts (view, copy link, defer, archive). Absent → no row menu is drawn. */
  onRowAction?: (action: string, item: ItemRow) => void;
  /** A row's own buttons in place of the row menu (the pool's "Why?" and remove). */
  renderRowActions?: (item: ItemRow) => React.ReactNode;
  /** Width of the trailing actions column. Default 40. */
  rowActionsWidth?: number;
  /** When false, no row checkboxes (a list with no bulk acts). Default true. */
  selectable?: boolean;
  /** Draw the rows as a ranked list — the pool's rank cell, drag and cut line (ListTable). */
  ranked?: ListTableRanked<ItemRow>;
  sort: SortSpec[];
  onSortChange: (specs: SortSpec[]) => void;
  expandedGroups: Set<string>;
  onToggleGroup: (key: string) => void;
  /** Cluster group ids currently expanded to show their members. */
  expandedClusters: Set<string>;
  onToggleCluster: (groupId: string) => void;
  searchQuery: string;
  queueNameMap?: Record<string, string>;
  queues?: { id: string; name: string }[];
  creatorNameMap?: Record<string, string>;
  ownerNameMap?: Record<string, string>;
  /** Per item: the value mark on its category and altitude (SPEC-value-mark-icon). */
  classificationMarks?: Record<string, ClassificationMarks>;
  visibleColumns: string[];
  /** The item currently open in the quick panel */
  selectedItemId?: string | null;
  /** Current view mode — affects row decoration */
  viewMode?: string;
  /** Called when the title cell is clicked directly (separate from row click) */
  onTitleClick?: (item: ItemRow) => void;
  /** Custom column widths (px) set by the user via drag-resize */
  columnWidths?: Record<string, number>;
  /** Called when user finishes dragging a column resize handle */
  onColumnResize?: (key: string, width: number) => void;
  /** Called on double-click of resize handle to reset to default */
  onColumnResetWidth?: (key: string) => void;
}

// ============================================================================
// Component
// ============================================================================

export function ItemTable({
  rows,
  grouped,
  density,
  selectedRows,
  onToggleRow,
  onSelectAll,
  onSelectRows,
  onClearSelection,
  onRowClick,
  onRowAction,
  renderRowActions,
  rowActionsWidth,
  selectable,
  ranked,
  onTitleClick,
  sort,
  onSortChange,
  expandedGroups,
  onToggleGroup,
  expandedClusters,
  onToggleCluster,
  searchQuery,
  queueNameMap,
  queues,
  creatorNameMap,
  ownerNameMap,
  classificationMarks,
  visibleColumns,
  selectedItemId,
  viewMode,
  columnWidths,
  onColumnResize,
  onColumnResetWidth,
}: ItemTableProps) {
  const altitudeText = useAltitudeText();
  // Inline editing
  const updateItem = useItemsStore((s) => s.updateItem);
  const [editingCell, setEditingCell] = useState<{ rowId: string; colKey: string } | null>(null);

  // Custom field attribute map — keyed by meta:FIELD_KEY
  const attributes = useAttributesStore((s) => s.attributes);
  const customFieldMap = useMemo(() => {
    const map: Record<string, { label: string; type: string }> = {};
    for (const a of attributes) {
      if (a.isCustomField) map[`meta:${a.key}`] = { label: a.label, type: a.type };
    }
    return map;
  }, [attributes]);

  const startEdit = useCallback((e: React.MouseEvent, rowId: string, colKey: string) => {
    e.stopPropagation();
    setEditingCell({ rowId, colKey });
  }, []);

  const commitEdit = useCallback((rowId: string, updates: Record<string, unknown>) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateItem(rowId, updates as any);
    setEditingCell(null);
  }, [updateItem]);

  const cancelEdit = useCallback(() => setEditingCell(null), []);

  const highlightMatch = useCallback(
    (text: string | undefined) => {
      if (!text) return null;
      if (!searchQuery) return text;
      const idx = text.toLowerCase().indexOf(searchQuery.toLowerCase());
      if (idx === -1) return text;
      return (
        <>
          {text.slice(0, idx)}
          <mark className="bg-[var(--mustard-dim)] text-[var(--mustard)] rounded px-0.5">{text.slice(idx, idx + searchQuery.length)}</mark>
          {text.slice(idx + searchQuery.length)}
        </>
      );
    },
    [searchQuery]
  );

  // Ordered visible column definitions.
  // Label comes from ALL_COLUMNS (single source of truth shared with the Columns panel),
  // falling back to COLUMN_CONFIG.label for table-only columns or the attribute label for meta:* columns.
  const orderedCols = visibleColumns
    .map((key) => {
      if (key.startsWith('meta:')) {
        const cf = customFieldMap[key];
        if (!cf) return null;
        return {
          key,
          label: cf.label,
          width: 'w-32',
          defaultWidth: 128,
          sortField: false as const,
          cellExtra: 'overflow-hidden',
        };
      }
      return {
        key,
        ...COLUMN_CONFIG[key],
        label: ALL_COLUMNS_LABEL[key] ?? COLUMN_CONFIG[key]?.label,
      };
    })
    .filter((c): c is NonNullable<typeof c> => c !== null && c.label !== undefined) as Array<{ key: string } & ColConfig>;

  // Columns that support click-to-edit (title stays as row-open affordance)
  const EDITABLE_COLS = new Set(['itemCategory', 'exit_status', 'altitude', 'queue', 'source', 'dedupeStatus']);

  // Display-only cell content — always returns the read view
  const renderDisplayContent = (key: string, item: ItemRow) => {
    // Custom fields — values live in item.metadata keyed by the field key (meta: prefix stripped)
    if (key.startsWith('meta:')) {
      const fieldKey = key.slice(5);
      const val = (item.metadata as Record<string, unknown> | undefined)?.[fieldKey];
      if (val === null || val === undefined || val === '') {
        return <span className="text-xs text-[var(--text-ter)]">—</span>;
      }
      if (Array.isArray(val)) {
        return (
          <div className="flex flex-wrap gap-0.5">
            {(val as unknown[]).map((v, i) => (
              <span key={i} className="text-[11px] px-1.5 py-0.5 rounded-full bg-primary-50 text-primary-700 border border-primary-200">
                {String(v)}
              </span>
            ))}
          </div>
        );
      }
      if (typeof val === 'boolean') {
        return (
          <span className={`text-xs font-medium ${val ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
            {val ? 'Yes' : 'No'}
          </span>
        );
      }
      return <span className="text-xs text-[var(--text-sec)] truncate">{String(val)}</span>;
    }

    switch (key) {
      case 'title': {
        // A settled cluster is one entry, under the cluster's name. Its members
        // are reachable by expanding it — they are not separate list entries.
        if (item.clusterEntry) {
          const { groupId, title, memberIds } = item.clusterEntry;
          const isExpanded = expandedClusters.has(groupId);
          return (
            <div className={`flex items-center gap-1.5 min-w-0 ${density === 'compact' ? 'text-xs' : 'text-sm'}`}>
              <ChevronRight
                className={`h-3.5 w-3.5 flex-shrink-0 text-[var(--text-ter)] transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
              />
              <Layers className="h-3.5 w-3.5 flex-shrink-0 text-[var(--text-ter)]" />
              <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate leading-tight">
                {highlightMatch(title)}
              </span>
              <span className="flex-shrink-0 font-mono text-[11px] uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-dim)] border border-[var(--rule)] dark:border-[var(--rule)] px-1 leading-normal">
                {memberIds.length} items
              </span>
            </div>
          );
        }

        const showGaps = viewMode === 'needs-shaping' || viewMode === 'triage';
        const missingCat = !item.itemCategory;
        const missingAlt = !item.altitudeLevel;
        return (
          <>
            <div className={`flex items-center gap-1.5 min-w-0 ${density === 'compact' ? 'text-xs' : 'text-sm'}`}>
              {item.clusterMemberOf && (
                <span
                  aria-hidden
                  className="flex-shrink-0 w-3 self-stretch border-l border-[var(--rule)] dark:border-[var(--rule)] ml-1 mr-1"
                />
              )}
              <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate leading-tight">
                {highlightMatch(item.title)}
              </span>
            </div>
            {density !== 'compact' && (showGaps && (missingCat || missingAlt)) && (
              <div className="flex items-center gap-1 mt-0.5">
                {missingCat && (
                  <span className="inline-flex items-center px-1.5 py-0 rounded text-[10px] font-medium bg-[var(--coral-dim)] text-[var(--coral)] border border-[var(--coral)]">
                    No category
                  </span>
                )}
                {missingAlt && viewMode === 'needs-shaping' && (
                  <span className="inline-flex items-center px-1.5 py-0 rounded text-[10px] font-medium bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)]">
                    No altitude
                  </span>
                )}
              </div>
            )}
            {density !== 'compact' && !showGaps && item.description && (
              <div className="text-xs text-[var(--text-ter)] truncate mt-0.5 leading-tight">{highlightMatch(item.description)}</div>
            )}
            {density === 'comfortable' && !showGaps && (
              <div className="flex items-center gap-1.5 mt-1.5">
                {item.hasChildren && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] text-[var(--text-ter)] bg-[var(--hover-tint)] rounded px-1 py-0.5" title="Has children">
                    <GitBranch className="h-2.5 w-2.5" />
                  </span>
                )}
                {item.parentItemId && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] text-[var(--text-ter)] bg-[var(--hover-tint)] rounded px-1 py-0.5" title="Has parent">
                    <Link2 className="h-2.5 w-2.5" />
                  </span>
                )}
                {item.tags?.slice(0, 3).map((tag) => (
                  <span key={tag} className="px-1.5 py-0.5 bg-[var(--hover-tint)] text-[var(--text-ter)] rounded text-[10px] font-medium">{tag}</span>
                ))}
              </div>
            )}
          </>
        );
      }
      case 'creationProvenance': {
        // A mark only while AI-generated and unconfirmed (owner rule, 2026-10-13).
        const hasSignal = authorshipNeedsMark({
          source: item.creationSource,
          reviewStatus: item.creationReviewStatus,
          provenance: item.creationProvenance,
        });
        if (!hasSignal) {
          return <span className="text-xs text-[var(--text-ter)]">—</span>;
        }
        return (
          <ItemCreationProvenanceBadge
            source={item.creationSource}
            reviewStatus={item.creationReviewStatus}
            provenance={item.creationProvenance}
            variant="icon"
            compact
          />
        );
      }
      case 'itemCategory': {
        const mark = classificationMarks?.[item.id]?.category;
        return item.itemCategory ? (
          <span className="inline-flex items-center gap-1">
            <CategoryBadge category={item.itemCategory} />
            {mark && <ValueMark {...mark} size={12} />}
          </span>
        ) : <span className="text-xs text-[var(--text-ter)] italic">—</span>;
      }
      case 'exit_status':
      case 'detailedState':
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return <StatusBadge state={(item as any).exit_status ?? 'active'} />;
      case 'altitude': {
        const mark = classificationMarks?.[item.id]?.altitude;
        return item.altitudeLevel ? (
          <span className="inline-flex items-center gap-1">
            <AltitudeIndicator level={item.altitudeLevel} category={item.itemCategory} showLabel className="py-0.5" />
            {mark && <ValueMark {...mark} size={12} />}
          </span>
        ) : <span className="text-xs text-[var(--text-ter)] italic">—</span>;
      }
      case 'readiness':
        return <BaseMissingCell item={item} />;
      case 'queue':
        return (
          <span className="text-xs text-[var(--text-ter)] truncate block">
            {item.queueId ? (queueNameMap?.[item.queueId] ?? 'General') : 'General'}
          </span>
        );
      case 'createdAt':
        return <span className="text-xs text-[var(--text-ter)] tabular-nums">{formatRelativeDate(item.createdAt)}</span>;
      case 'updatedAt':
        return item.updatedAt
          ? <span className="text-xs text-[var(--text-ter)] tabular-nums">{formatRelativeDate(item.updatedAt)}</span>
          : <span className="text-xs text-[var(--text-ter)]">—</span>;
      case 'createdBy':
        return (
          <span className="text-xs text-[var(--text-ter)] truncate block">
            {item.createdBy && creatorNameMap?.[item.createdBy]
              ? creatorNameMap[item.createdBy]
              : <span className="text-[var(--text-ter)]">—</span>}
          </span>
        );
      case 'owner': {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ownerId = (item as any).ownerId as string | undefined;
        const ownerName = ownerId && ownerNameMap?.[ownerId] ? ownerNameMap[ownerId] : null;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const isDelegated = (item as any).ownershipMode === 'delegated';
        return (
          <div className="flex items-center gap-1 min-w-0">
            {ownerName ? (
              <span className="text-xs text-[var(--text-sec)] truncate">{ownerName}</span>
            ) : (
              <span className="text-xs text-[var(--text-ter)]">—</span>
            )}
            {isDelegated && (
              <span className="text-[10px] text-[var(--mustard)] bg-[var(--mustard-dim)] rounded px-1 py-0.5 flex-shrink-0">delegated</span>
            )}
          </div>
        );
      }
      case 'source':
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (item as any).source
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ? <span className="text-xs text-[var(--text-ter)] truncate block capitalize">{(item as any).source}</span>
          : <span className="text-xs text-[var(--text-ter)] italic">—</span>;
      case 'strategicBucket': {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const bucket = (item as any).strategicBucket as string | undefined;
        const BUCKET_STYLES: Record<string, string> = {
          growth:      'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
          commitments: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
          reliability: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
          platform:    'bg-[var(--slate-dim)] text-[var(--text-sec)]',
          compliance:  'bg-[var(--coral-dim)] text-[var(--coral)]',
        };
        return bucket
          ? <span className={`inline-block px-2 py-0.5  text-[11px] font-medium capitalize ${BUCKET_STYLES[bucket] ?? 'bg-[var(--hover-tint)] text-[var(--text-sec)]'}`}>{bucket}</span>
          : <span className="text-xs text-[var(--text-ter)]">—</span>;
      }
      case 'tags':
        return item.tags && item.tags.length > 0 ? (
          <div className="flex gap-1 overflow-hidden">
            {item.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="flex-shrink-0 px-1.5 py-0.5 bg-[var(--hover-tint)] text-[var(--text-ter)] rounded text-[10px] font-medium truncate max-w-[70px]">{tag}</span>
            ))}
            {item.tags.length > 2 && <span className="flex-shrink-0 text-[10px] text-[var(--text-ter)]">+{item.tags.length - 2}</span>}
          </div>
        ) : <span className="text-xs text-[var(--text-ter)]">—</span>;
      case 'collections':
        return item.collectionNames && item.collectionNames.length > 0 ? (
          <div className="flex gap-1 overflow-hidden">
            {item.collectionNames.slice(0, 2).map((name) => (
              <span key={name} className="flex-shrink-0 px-1.5 py-0.5 bg-[var(--slate-dim)] text-[var(--text-sec)] rounded text-[10px] font-medium truncate max-w-[80px]">{name}</span>
            ))}
            {item.collectionNames.length > 2 && <span className="flex-shrink-0 text-[10px] text-[var(--text-ter)]">+{item.collectionNames.length - 2}</span>}
          </div>
        ) : <span className="text-xs text-[var(--text-ter)]">—</span>;
      case 'relationships': {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const hasLineage = item.dedupeStatus === 'split' || !!(item as any).parentItemId || item.hasLineage;
        const present = REL_CHIPS.filter(({ field }) => {
          if (field === 'hasLineage') return hasLineage;
          return !!(item as Record<string, unknown>)[field];
        });
        // One chip per relationship family (role-aware fields share a guideId).
        const byGuide = new Map<string, (typeof present)[number]>();
        for (const chip of present) {
          if (!byGuide.has(chip.guideId)) byGuide.set(chip.guideId, chip);
        }
        const unique = [...byGuide.values()];
        if (unique.length === 0) return <span className="text-xs text-[var(--text-ter)]">—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {unique.map((chip) => {
              const guide = getRelationshipGuideEntry(chip.guideId);
              const Icon = guide?.Icon;
              return (
                <span
                  key={chip.guideId}
                  title={guide?.label ?? chip.guideId}
                  aria-label={guide?.label ?? chip.guideId}
                  className={`inline-flex items-center justify-center p-1 rounded border ${relationshipChipClass(chip.guideId)}`}
                >
                  {Icon && <Icon className="h-2.5 w-2.5 shrink-0" />}
                </span>
              );
            })}
          </div>
        );
      }
      case 'dedupeStatus':
        return item.dedupeStatus && item.dedupeStatus !== 'pending' ? (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${
            item.dedupeStatus === 'unique' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
            : item.dedupeStatus === 'duplicate_of' ? 'bg-[var(--coral-dim)] text-[var(--coral)]'
            : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
          }`}>
            {item.dedupeStatus.replace(/_/g, ' ')}
          </span>
        ) : <span className="text-[10px] text-[var(--text-ter)] italic">—</span>;
      case 'triage_action':
        return (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              title="Pursue — accept and start item work"
              onClick={(e) => { e.stopPropagation(); onRowAction?.('triage:pursue', item); }}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)] border border-[var(--green)] transition-colors"
            >
              <Check className="h-3 w-3 flex-shrink-0" />
              {density !== 'compact' && <span>Pursue</span>}
            </button>
            <button
              type="button"
              title="Defer — set aside for later"
              onClick={(e) => { e.stopPropagation(); onRowAction?.('triage:defer', item); }}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium bg-[var(--mustard-dim)] text-[var(--mustard)] hover:bg-[var(--mustard-dim)] border border-[var(--mustard)] transition-colors"
            >
              <Clock className="h-3 w-3 flex-shrink-0" />
              {density !== 'compact' && <span>Defer</span>}
            </button>
            <button
              type="button"
              title="Archive — remove from consideration"
              onClick={(e) => { e.stopPropagation(); onRowAction?.('triage:reject', item); }}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium bg-[var(--surface)] text-[var(--text-ter)] hover:bg-[var(--coral-dim)] hover:text-[var(--coral)] border border-[var(--rule)] hover:border-[var(--coral)] transition-colors"
            >
              <Archive className="h-3 w-3 flex-shrink-0" />
              {density !== 'compact' && <span>Archive</span>}
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  // Main cell renderer — switches between editor and display
  const renderCellContent = (key: string, item: ItemRow) => {
    const isEditing = editingCell?.rowId === item.id && editingCell.colKey === key;

    if (isEditing) {
      switch (key) {
        case 'itemCategory':
          return (
            <InlineSelect
              value={item.itemCategory ?? ''}
              options={[
                { value: '',            label: '— none —' },
                { value: 'problem',     label: 'Problem' },
                { value: 'opportunity', label: 'Opportunity' },
                { value: 'risk',        label: 'Risk' },
                { value: 'solution',    label: 'Solution' },
                { value: 'commitment',  label: 'Commitment' },
                { value: 'investment',  label: 'Investment' },
              ]}
              onSave={(v) => commitEdit(item.id, { itemCategory: v || null })}
              onCancel={cancelEdit}
            />
          );
        case 'exit_status':
          return (
            <InlineSelect
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              value={(item as any).exit_status ?? 'active'}
              options={[
                { value: 'active',   label: 'Active' },
                { value: 'deferred', label: 'Deferred' },
                { value: 'archived', label: 'Archived' },
              ]}
              onSave={(v) => commitEdit(item.id, { exit_status: v })}
              onCancel={cancelEdit}
            />
          );
        case 'altitude':
          return (
            <InlineSelect
              value={item.altitudeLevel ? String(item.altitudeLevel) : ''}
              options={[
                { value: '', label: '— none —' },
                ...ALTITUDE_LEVELS_ORDERED.map((lvl) => ({
                  value: String(lvl),
                  label: altitudeText.format(lvl, item.itemCategory, { short: true }) ?? String(lvl),
                })),
              ]}
              onSave={(v) => commitEdit(item.id, { altitudeLevel: v ? Number(v) : null })}
              onCancel={cancelEdit}
            />
          );
        case 'queue':
          return (
            <InlineSelect
              value={item.queueId ?? ''}
              options={[
                { value: '', label: 'General' },
                ...(queues ?? []).map((q) => ({ value: q.id, label: q.name })),
              ]}
              onSave={(v) => commitEdit(item.id, { queueId: v || null })}
              onCancel={cancelEdit}
            />
          );
        case 'source':
          return (
            <InlineText
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              value={(item as any).source ?? ''}
              placeholder="Add source…"
              onSave={(v) => commitEdit(item.id, { source: v || null })}
              onCancel={cancelEdit}
            />
          );
        case 'dedupeStatus':
          return (
            <InlineSelect
              value={item.dedupeStatus ?? ''}
              options={[
                { value: '',            label: '— pending —' },
                { value: 'unique',      label: 'Unique' },
                { value: 'duplicate_of', label: 'Duplicate of…' },
                { value: 'merged',      label: 'Merged' },
              ]}
              onSave={(v) => commitEdit(item.id, { dedupeStatus: v || null })}
              onCancel={cancelEdit}
            />
          );
      }
    }

    // A cluster entry is not an item: its title cell expands/collapses the
    // cluster, and no cell edits in place (a cell there shows the cluster's
    // representative, so editing it would silently mutate one member).
    if (item.clusterEntry) {
      if (key === 'title') {
        const { groupId, memberIds } = item.clusterEntry;
        const isExpanded = expandedClusters.has(groupId);
        return (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleCluster(groupId); }}
            aria-expanded={isExpanded}
            title={`${isExpanded ? 'Collapse' : 'Expand'} cluster (${memberIds.length} items)`}
            className="flex items-center gap-1 min-w-0 w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
          >
            <div className="min-w-0 flex-1">{renderDisplayContent(key, item)}</div>
          </button>
        );
      }
      return renderDisplayContent(key, item);
    }

    // The title cell — and only the title cell — opens Item Detail. Every other
    // cell stays as it was: editable columns edit in place, the rest are inert.
    // Making the whole ROW navigate broke that: any click anywhere threw you out
    // of the list, so you could not scan or click near a field without leaving.
    if (key === 'title' && onTitleClick) {
      return (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onTitleClick(item); }}
          title={`Open ${item.title}`}
          className="flex items-center gap-1 min-w-0 w-full text-left hover:underline underline-offset-2 decoration-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
        >
          <div className="min-w-0 flex-1">{renderDisplayContent(key, item)}</div>
        </button>
      );
    }

    // Display mode — editable columns wrap in a button for accessibility + Playwright
    if (EDITABLE_COLS.has(key)) {
      return (
        <button
          type="button"
          onClick={(e) => startEdit(e, item.id, key)}
          className="group/cell flex items-center gap-1 min-w-0 w-full text-left"
          title="Click to edit"
        >
          <div className="min-w-0 flex-1">{renderDisplayContent(key, item)}</div>
          <Pencil className="h-2.5 w-2.5 text-[var(--text-ter)] flex-shrink-0 opacity-0 group-hover/cell:opacity-100 transition-opacity" />
        </button>
      );
    }

    return renderDisplayContent(key, item);
  };

  return (
    <ListTable
      rows={rows}
      getId={(r) => r.id}
      columns={orderedCols.map((col) => ({
        key: col.key,
        label: col.label,
        sortField: col.sortField,
        defaultWidth: col.defaultWidth,
        width: col.width,
        cellExtra: col.cellExtra,
        render: (item) => renderCellContent(col.key, item),
      }))}
      density={density}
      selectedIds={selectedRows}
      onToggleRow={onToggleRow}
      onSelectAll={onSelectAll}
      onSelectRows={onSelectRows}
      onClearSelection={onClearSelection}
      onRowClick={onRowClick}
      sort={sort}
      onSortChange={onSortChange}
      selectedRowId={selectedItemId}
      grouped={grouped}
      expandedGroups={expandedGroups}
      onToggleGroup={onToggleGroup}
      searchQuery={searchQuery}
      columnWidths={columnWidths}
      onColumnResize={onColumnResize}
      onColumnResetWidth={onColumnResetWidth}
      renderRowActions={
        renderRowActions ??
        (onRowAction
          ? (item) =>
              // Cluster entries have no per-item actions — view/copy-link/defer/archive
              // are ambiguous across members. Expand to act on a member.
              item.clusterEntry ? null : <RowActions item={item} onAction={onRowAction} />
          : undefined)
      }
      rowActionsWidth={rowActionsWidth}
      selectable={selectable}
      ranked={ranked}
      getCellClassName={(item, col) =>
        editingCell?.rowId === item.id && editingCell.colKey === col.key
          ? 'ring-1 ring-inset ring-indigo-300 rounded bg-[var(--bg-raised)] dark:bg-[var(--surface)]'
          : undefined
      }
    />
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function CategoryBadge({ category }: { category: string }) {
  return (
    <span className="text-xs text-[var(--text-sec)]">
      {category.charAt(0).toUpperCase() + category.slice(1)}
    </span>
  );
}

function StatusBadge({ state }: { state: string }) {
  return <span className="text-xs text-[var(--text-sec)]">{STATE_LABELS[state] ?? state}</span>;
}

function BaseMissingCell({ item }: { item: ItemRow }) {
  const hasTitle    = typeof item.title === 'string' && item.title.trim().length > 0;
  const hasCategory = Boolean(item.itemCategory);
  const hasAltitude = Boolean(item.altitudeLevel);

  const missing: { label: string; critical: boolean }[] = [];
  if (!hasTitle)    missing.push({ label: 'title',    critical: true });
  if (!hasCategory) missing.push({ label: 'category', critical: true });
  if (!hasAltitude) missing.push({ label: 'altitude', critical: false });

  if (missing.length === 0) {
    return <span className="text-xs text-[var(--text-ter)]">—</span>;
  }

  return (
    <div className="flex flex-wrap gap-0.5">
      {missing.map(({ label, critical }) => (
        <span
          key={label}
          className={`inline-flex items-center px-1.5 py-0 rounded text-[10px] font-medium ${
            critical
              ? 'bg-[var(--coral-dim)] text-[var(--coral)] border border-[var(--coral)]'
              : 'bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)]'
          }`}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

// ============================================================================
// Inline edit sub-components
// ============================================================================

function InlineText({
  value,
  placeholder,
  onSave,
  onCancel,
}: {
  value: string;
  placeholder?: string;
  onSave: (v: string) => void;
  onCancel: () => void;
}) {
  const [val, setVal] = useState(value);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);

  const commit = () => {
    const trimmed = val.trim();
    if (trimmed !== value) onSave(trimmed);
    else onCancel();
  };

  return (
    <input
      ref={ref}
      value={val}
      placeholder={placeholder}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        if (e.key === 'Escape') { e.stopPropagation(); onCancel(); }
      }}
      onClick={(e) => e.stopPropagation()}
      className="w-full text-xs text-[var(--text-pri)] bg-transparent border-0 outline-none focus:outline-none placeholder-gray-300 py-0.5"
    />
  );
}

function InlineSelect({
  value,
  options,
  onSave,
  onCancel,
}: {
  value: string;
  options: { value: string; label: string }[];
  onSave: (v: string) => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLSelectElement>(null);
  const saved = useRef(false);
  useEffect(() => { ref.current?.focus(); }, []);

  return (
    <select
      ref={ref}
      defaultValue={value}
      onChange={(e) => { saved.current = true; onSave(e.target.value); }}
      onBlur={() => { if (!saved.current) onCancel(); }}
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onCancel(); } }}
      onClick={(e) => e.stopPropagation()}
      className="no-dark w-full text-xs text-[var(--text-pri)] dark:text-[var(--text-pri)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] border-0 outline-none focus:outline-none cursor-pointer py-0.5 -mx-0.5"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function RowActions({
  item,
  onAction,
}: {
  item: ItemRow;
  onAction: (action: string, item: ItemRow) => void;
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setOpen((v) => !v);
  };

  const handleClose = () => setOpen(false);

  return (
    <div>
      <button
        ref={buttonRef}
        onClick={handleOpen}
        className={`p-1  transition-all duration-150 ${
          open
            ? 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
            : 'text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--hover-tint)] opacity-0 group-hover:opacity-100'
        }`}
        aria-label="Row actions"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && menuPos && createPortal(
        <>
          <div className="fixed inset-0 z-[100]" onClick={handleClose} />
          <div
            className="fixed z-[101] py-1 w-44 bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] shadow-lg shadow-gray-200/50 dark:shadow-none border border-[var(--rule)]/80 dark:border-[var(--rule)]"
            style={{ top: menuPos.top, right: menuPos.right }}
          >
            <ActionItem
              icon={<ExternalLink className="h-3.5 w-3.5" />}
              label="View detail"
              onClick={(e) => { e.stopPropagation(); handleClose(); onAction('view', item); }}
            />
            <ActionItem
              icon={<Copy className="h-3.5 w-3.5" />}
              label="Copy link"
              onClick={(e) => { e.stopPropagation(); handleClose(); onAction('copy-link', item); }}
            />
            <div className="my-1 mx-2 border-t border-[var(--rule)]" />
            <ActionItem
              icon={<Clock className="h-3.5 w-3.5" />}
              label="Defer"
              onClick={(e) => { e.stopPropagation(); handleClose(); onAction('defer', item); }}
            />
            <ActionItem
              icon={<Archive className="h-3.5 w-3.5" />}
              label="Archive"
              onClick={(e) => { e.stopPropagation(); handleClose(); onAction('archive', item); }}
              destructive
            />
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

function ActionItem({
  icon,
  label,
  onClick,
  destructive,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: (e: React.MouseEvent) => void;
  destructive?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-sm transition-colors ${
        destructive
          ? 'text-[var(--coral)] hover:bg-[var(--coral-dim)]'
          : 'text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function formatRelativeDate(date: Date | string): string {
  const d = date instanceof Date ? date : new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
